/**
 * B502 — tenant staff management doors (UNGATED 2026-09-21, Pathik: "for
 * tenants they dont have a way to manage their employees as well with
 * permission right? we should add that as well"). Spec = Figma 338-2
 * (Back office — Staff & permissions). ADMIN-only, riding the existing
 * human pool (immutable custom:tenantId); the permission matrix itself is
 * the authorizer's role tables — these doors edit WHO holds a role, never
 * what a role may do.
 */
import { z } from "zod";
import { readOp, writeOp } from "./common.js";

export const zStaffRole = z.enum(["ADMIN", "MANAGER", "SELLER", "VALIDATOR"]);

const zStaffMember = z
  .object({
    staffId: z.string().min(1),
    email: z.string().min(3),
    /** Display name, when the invite carried one. */
    name: z.string().min(1).optional(),
    role: zStaffRole,
    enabled: z.boolean(),
    /** Has the invite been accepted (password set)? */
    confirmed: z.boolean(),
    /** "Where they work" — tenant words, tenant configuration. */
    locations: z.array(z.string().min(1)).optional(),
  })
  .strict();

const EXAMPLE_MEMBER = {
  staffId: "b1c2d3e4-staff",
  email: "amy@harborferries.example",
  role: "SELLER",
  enabled: true,
  confirmed: true,
  locations: ["Harbor kiosk"],
};

export const staffList = readOp({
  name: "staff.list",
  permission: "staff:read",
  principals: ["STAFF:ADMIN"],
  fields: {},
  response: z.object({ staff: z.array(zStaffMember) }).strict(),
  exampleRequest: {},
  exampleResponse: { staff: [EXAMPLE_MEMBER] },
});

export const staffInvite = writeOp({
  name: "staff.invite",
  permission: "staff:write",
  principals: ["STAFF:ADMIN"],
  fields: {
    email: z.string().min(3).max(320),
    role: zStaffRole,
    name: z.string().min(1).max(120).optional(),
    locations: z.array(z.string().min(1).max(120)).max(20).optional(),
  },
  response: z
    .object({
      member: zStaffMember,
      /** Shown ONCE (SES is a go-live item) — never stored, never re-readable. */
      temporaryPassword: z.string().min(1),
    })
    .strict(),
  exampleRequest: { email: "amy@harborferries.example", role: "SELLER" },
  exampleResponse: { member: { ...EXAMPLE_MEMBER, confirmed: false }, temporaryPassword: "shown-once" },
});

export const staffUpdate = writeOp({
  name: "staff.update",
  permission: "staff:write",
  principals: ["STAFF:ADMIN"],
  fields: {
    staffId: z.string().min(1),
    role: zStaffRole.optional(),
    locations: z.array(z.string().min(1).max(120)).max(20).optional(),
  },
  response: z.object({ member: zStaffMember }).strict(),
  exampleRequest: { staffId: "b1c2d3e4-staff", role: "MANAGER" },
  exampleResponse: { member: { ...EXAMPLE_MEMBER, role: "MANAGER" } },
});

export const staffDisable = writeOp({
  name: "staff.disable",
  permission: "staff:write",
  principals: ["STAFF:ADMIN"],
  fields: { staffId: z.string().min(1) },
  response: z.object({ member: zStaffMember }).strict(),
  exampleRequest: { staffId: "b1c2d3e4-staff" },
  exampleResponse: { member: { ...EXAMPLE_MEMBER, enabled: false } },
});

export const staffEnable = writeOp({
  name: "staff.enable",
  permission: "staff:write",
  principals: ["STAFF:ADMIN"],
  fields: { staffId: z.string().min(1) },
  response: z.object({ member: zStaffMember }).strict(),
  exampleRequest: { staffId: "b1c2d3e4-staff" },
  exampleResponse: { member: EXAMPLE_MEMBER },
});

export const staffRemove = writeOp({
  name: "staff.remove",
  permission: "staff:write",
  principals: ["STAFF:ADMIN"],
  fields: { staffId: z.string().min(1) },
  response: z.object({ removed: z.literal(true) }).strict(),
  exampleRequest: { staffId: "b1c2d3e4-staff" },
  exampleResponse: { removed: true },
});
